/** App — root component with routing. */

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/auth/AuthProvider";
import { RequireAuth } from "@/auth/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { LandingPage } from "@/pages/Landing";
import { LoginPage } from "@/pages/Login";
import { DashboardPage } from "@/pages/Dashboard";
import { CompetitionViewPage } from "@/pages/CompetitionView";
import { SetupPage } from "@/pages/Setup";
import { ProducerPage } from "@/pages/Producer";
import { JudgePage } from "@/pages/Judge";
import { RidersPage } from "@/pages/Riders";
import { TrainingPage, CoursePage } from "@/pages/Training";
import { UpgradePage } from "@/pages/Upgrade";
import { OBSOverlayPage } from "@/pages/OBSOverlay";
import { InviteAcceptPage } from "@/pages/InviteAccept";
import { QuizPage } from "@/pages/QuizPage";
import { useAuthStore } from "@/stores/auth";
import type { ReactNode } from "react";

/** If user is logged in, redirect to dashboard. Otherwise show children (landing page). */
function AuthRedirect({ children }: { children: ReactNode }) {
  const { user, loading } = useAuthStore();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<AuthRedirect><LandingPage /></AuthRedirect>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/obs/:token" element={<OBSOverlayPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />

          {/* Authenticated full-screen routes (no AppShell) */}
          <Route path="/quiz/:courseType" element={<RequireAuth><QuizPage /></RequireAuth>} />

          {/* Authenticated routes */}
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/competition/:competitionId" element={<CompetitionViewPage />} />
            <Route path="/competition/:competitionId/setup" element={<SetupPage />} />
            <Route path="/competition/:competitionId/producer" element={<ProducerPage />} />
            <Route path="/competition/:competitionId/judge" element={<JudgePage />} />
            <Route path="/riders" element={<RidersPage />} />
            <Route path="/training" element={<TrainingPage />} />
            <Route path="/training/:courseType" element={<CoursePage />} />
            <Route path="/upgrade" element={<UpgradePage />} />
            <Route path="/support" element={<Navigate to="/training" replace />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
